import { useTranslation } from 'react-i18next';
import type { SubBrandModule } from 'types/capi/modules';
import type {
  Image as ImageType,
  InternalLink as CapiInternalLink
} from 'types/capi/shared';

import Image, { smallImageOptions } from '~/components/Image';
import Tracked from '~/components/Tracked';

function CarouselItem({
  image,
  link
}: {
  image: ImageType;
  link: CapiInternalLink;
}) {
  return (
    <Tracked.InternalLink
      to={link.path}
      variant="none"
      pagePosition="Homepage Top Silhouettes"
      name="SILHOUETTE_CARD"
    >
      <div className="flex flex-col items-center gap-4">
        <div className="flex size-28 items-center justify-center text-clip rounded-full bg-white px-4 py-7 md:size-[160px] md:pb-5 md:pt-3">
          <Image
            src={image.url}
            className="w-full rounded-full"
            alt={image.alt}
            loading="lazy"
            {...smallImageOptions({ pad: true })}
          />
        </div>
        <div className="leading-6">{link.text}</div>
      </div>
    </Tracked.InternalLink>
  );
}

type Props = { subBrands: SubBrandModule['items'] };

function Carousel({ subBrands }: Props) {
  return (
    <div className="no-scrollbar flex items-start gap-2 self-stretch overflow-x-scroll pb-4 max-lg:px-11 max-md:-mx-4 max-md:px-4 md:justify-between">
      {subBrands.map((sb) => (
        <CarouselItem image={sb.image} link={sb.link} key={sb.link.text} />
      ))}
    </div>
  );
}

export default function SubBrands({ subBrands }: Props) {
  const { t } = useTranslation('home');

  return (
    <div className="w-full bg-concrete">
      <div className="flex flex-col items-center justify-center gap-6 py-10 max-md:px-4 md:mx-auto md:max-w-desktop md:py-20">
        <div className="flex flex-col justify-center gap-6 self-stretch py-0">
          <h2 className="px-4 text-center text-[32px] leading-10 md:text-[40px] md:leading-[48px]">
            {t('browseMoreTopSilhouettes')}
          </h2>
          <Carousel subBrands={subBrands} />
        </div>
      </div>
    </div>
  );
}
